import { useState } from 'react';
import { useFormacao, type FormacaoParticipante } from '@/context/FormacaoContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import CertificadoUploadSection from './CertificadoUploadSection';

interface Props {
  participante: FormacaoParticipante & { nomeFormacao?: string };
  onClose: () => void;
  onSuccess?: () => void;
}

const ESTADOS_PARTICIPACAO = ['Inscrito', 'A Frequentar', 'Concluído', 'Desistiu', 'Reprovado'];

export default function EditarParticipacaoModal({ participante, onClose, onSuccess }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { atualizarParticipante, saving } = useFormacao();

  const [estadoParticipacao, setEstadoParticipacao] = useState(participante.estado_participacao);
  const [notaFinal, setNotaFinal] = useState(participante.nota_final ?? '');
  const [custo, setCusto] = useState<number | ''>(participante.custo ?? '');
  const [certificadoEmitido, setCertificadoEmitido] = useState(participante.certificado_emitido);
  const [dataCertificado, setDataCertificado] = useState(participante.data_certificado ?? '');
  const [observacoes, setObservacoes] = useState(participante.observacoes ?? '');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const nome = participante.funcionario_nome ?? participante.funcionario_id;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const ok = await atualizarParticipante(participante.id, {
      estado_participacao: estadoParticipacao,
      nota_final: notaFinal.trim() || null,
      custo: custo === '' ? null : Number(custo),
      certificado_emitido: certificadoEmitido,
      data_certificado: dataCertificado || null,
      observacoes: observacoes.trim() || null,
    });
    if (ok) {
      onSuccess?.();
      onClose();
    } else {
      setSubmitError('Não foi possível guardar. Tente novamente.');
    }
  }

  const estadoCores: Record<string, string> = {
    'Inscrito':     'text-zinc-600 bg-zinc-100 border-zinc-200',
    'A Frequentar': 'text-amber-700 bg-amber-50 border-amber-200',
    'Concluído':    'text-emerald-700 bg-emerald-50 border-emerald-200',
    'Desistiu':     'text-rose-600 bg-rose-50 border-rose-200',
    'Reprovado':    'text-orange-700 bg-orange-50 border-orange-200',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto mx-4 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-start justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 flex-shrink-0 mt-0.5">
              <i className="ri-edit-line text-white text-base" />
            </div>
            <div className="min-w-0">
              <h2 className="text-[16px] font-bold text-zinc-900">Editar Participação</h2>
              <p className="text-[11px] text-zinc-400 truncate max-w-[280px]">
                {nome}
                {participante.nomeFormacao && (
                  <span className="text-zinc-300"> · {participante.nomeFormacao}</span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-5 space-y-5">
          {/* Estado */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-2">Estado de Participação</label>
            <div className="flex flex-wrap gap-2">
              {ESTADOS_PARTICIPACAO.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setEstadoParticipacao(s)}
                  className={`whitespace-nowrap text-[12px] font-semibold px-3 py-1.5 rounded-full border transition-all cursor-pointer ${
                    estadoParticipacao === s
                      ? estadoCores[s] ?? 'text-zinc-600 bg-zinc-100 border-zinc-200'
                      : 'text-zinc-400 bg-white border-zinc-100 hover:border-zinc-300'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Custo da formação por participante */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Custo da Formação (€) <span className="text-[10px] font-normal text-zinc-400">opcional</span>
            </label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-zinc-400">€</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={custo}
                onChange={(e) => setCusto(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Ex: 150"
                className="w-full pl-7 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
              />
            </div>
          </div>

          {/* Nota final */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Nota Final</label>
            <input
              type="text"
              value={notaFinal}
              onChange={(e) => setNotaFinal(e.target.value)}
              placeholder="Ex: Aprovado, 16 valores, Satisfaz..."
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
            />
          </div>

          {/* Certificado emitido + data */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCertificadoEmitido((v) => !v)}
                className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative flex-shrink-0 ${certificadoEmitido ? 'bg-emerald-500' : 'bg-zinc-200'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${certificadoEmitido ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
              <label
                className="text-[13px] font-medium text-zinc-700 cursor-pointer"
                onClick={() => setCertificadoEmitido((v) => !v)}
              >
                Certificado emitido
              </label>
            </div>

            {certificadoEmitido && (
              <div>
                <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Data do Certificado</label>
                <input
                  type="date"
                  value={dataCertificado}
                  onChange={(e) => setDataCertificado(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
                />
              </div>
            )}
          </div>

          {/* Upload de certificado */}
          <div>
            <CertificadoUploadSection
              participanteId={participante.id}
              formacaoId={participante.formacao_id}
              certificadoUrl={participante.certificado_url ?? null}
              certificadoEmitido={certificadoEmitido}
              compact
            />
          </div>

          {/* Observações */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Observações</label>
            <textarea
              rows={2}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              maxLength={500}
              placeholder="Notas adicionais sobre esta participação..."
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 resize-none"
            />
            <p className="text-[10px] text-zinc-400 text-right mt-0.5">{observacoes.length}/500</p>
          </div>

          {submitError && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 text-[12px] px-3 py-2 rounded-lg">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-sm" />
              </div>
              {submitError}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
            <button
              type="button"
              onClick={handleClose}
              className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>
                  A guardar...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line text-sm" /></div>
                  Guardar
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
